import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import MediaTile from './MediaTile';
import MediaTileSkeleton from './MediaTileSkeleton';
import type { ProfileMediaItem } from '../../services/mediaContracts';

interface MediaGridProps {
  items: ProfileMediaItem[];
  selectedIds: Set<string>;
  selectionMode: boolean;
  loading?: boolean;
  loadingMore?: boolean;
  /** Stable sentinel after the grid — only pass when more pages remain. */
  scrollSentinelRef?: React.RefObject<HTMLDivElement | null>;
  onToggleSelect: (item: ProfileMediaItem) => void;
  onOpen: (item: ProfileMediaItem, index: number) => void;
  onDownload: (item: ProfileMediaItem) => void;
  downloadingKey?: string | null;
}

const itemKey = (item: ProfileMediaItem) =>
  `${item.tweetId}-${item.downloadUrl}`;

const GAP_PX = 16;
const GAP_PX_MOBILE = 12;
const OVERSCAN_ROWS = 3;

const columnCountForWidth = (width: number): number => {
  if (width < 600) return 2;
  if (width < 900) return 3;
  return 4;
};

const MediaGrid: React.FC<MediaGridProps> = ({
  items,
  selectedIds,
  selectionMode,
  loading,
  loadingMore,
  scrollSentinelRef,
  onToggleSelect,
  onOpen,
  onDownload,
  downloadingKey,
}) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const measure = () => {
      setWidth(el.clientWidth);
      setScrollMargin(el.getBoundingClientRect().top + window.scrollY);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [loading, items.length]);

  const columnCount = columnCountForWidth(width || 360);
  const gap = width > 0 && width < 600 ? GAP_PX_MOBILE : GAP_PX;
  const cellSize =
    width > 0
      ? Math.max(80, (width - gap * (columnCount - 1)) / columnCount)
      : 160;
  const rowHeight = cellSize + gap;
  const rowCount = Math.ceil(items.length / columnCount) || 0;

  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN_ROWS,
    scrollMargin,
  });

  // Keep row size in sync when the container width / column count changes.
  useEffect(() => {
    virtualizer.measure();
  }, [rowHeight, columnCount, virtualizer]);

  const virtualRows = virtualizer.getVirtualItems();

  const skeletonGrid = useMemo(
    () => (
      <Box
        role="status"
        aria-busy="true"
        aria-label="Loading media"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {Array.from({ length: 16 }).map((_, i) => (
          <MediaTileSkeleton key={i} index={i} />
        ))}
      </Box>
    ),
    []
  );

  if (loading && items.length === 0) {
    return skeletonGrid;
  }

  return (
    <Box ref={listRef}>
      <Box
        aria-busy={loadingMore || undefined}
        aria-label={loadingMore ? 'Loading more media' : undefined}
        sx={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualRows.map((virtualRow) => {
          const start = virtualRow.index * columnCount;
          const rowItems = items.slice(start, start + columnCount);
          return (
            <Box
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: cellSize,
                transform: `translateY(${
                  virtualRow.start - virtualizer.options.scrollMargin
                }px)`,
                display: 'grid',
                gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                gap: `${gap}px`,
              }}
            >
              {rowItems.map((item, col) => {
                const index = start + col;
                const key = itemKey(item);
                return (
                  <MediaTile
                    key={key}
                    item={item}
                    index={index}
                    selected={selectedIds.has(key)}
                    selectionMode={selectionMode || selectedIds.size > 0}
                    onToggleSelect={() => onToggleSelect(item)}
                    onOpen={() => onOpen(item, index)}
                    onDownload={() => onDownload(item)}
                    downloading={downloadingKey === key}
                  />
                );
              })}
            </Box>
          );
        })}
      </Box>

      {loadingMore && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            gap: `${gap}px`,
            mt: 2,
          }}
        >
          {Array.from({ length: columnCount * 2 }).map((_, i) => (
            <MediaTileSkeleton key={`loading-more-${i}`} index={i} quiet />
          ))}
        </Box>
      )}

      {scrollSentinelRef && (
        <Box
          ref={scrollSentinelRef}
          sx={{
            height: 1,
            width: '100%',
            pointerEvents: 'none',
            mt: 1,
          }}
          aria-hidden
        />
      )}
    </Box>
  );
};

export { itemKey };
export default MediaGrid;
